import React, { useState, useRef, useEffect } from 'react';
import ProductCard from './shop/ProductCard';
import '../styles/ProductCarousel.css';

export default function ProductCarousel({
  products = [],
  title,
  subtitle,
  minimal = false,
}) {
  const carouselRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Actualizar progreso y estado de flechas en scroll
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleScroll = () => {
      const scrollLeft = carousel.scrollLeft;
      const maxScroll = carousel.scrollWidth - carousel.clientWidth;

      if (maxScroll > 0) {
        const progress = (scrollLeft / maxScroll) * 100;
        setScrollProgress(progress);
        setCanScrollLeft(scrollLeft > 10);
        setCanScrollRight(scrollLeft < maxScroll - 10);
      } else {
        setCanScrollLeft(false);
        setCanScrollRight(false);
      }
    };

    handleScroll();
    carousel.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    return () => {
      carousel.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [products]);

  // Avanza ~85% del ancho visible: equivale a una "página" del carrusel,
  // así con dos clics recorres todo aunque haya 10+ productos.
  const scroll = (direction) => {
    if (!carouselRef.current) return;
    const viewport = carouselRef.current.clientWidth;
    const scrollAmount = Math.max(320, viewport * 0.85);
    carouselRef.current.scrollBy({
      left: direction === 'next' ? scrollAmount : -scrollAmount,
      behavior: 'smooth',
    });
  };

  if (!products || products.length === 0) return null;

  return (
    <div
      className={`pc-carousel${minimal ? ' pc-carousel--minimal' : ''}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onTouchStart={() => setIsHovering(true)}
    >
      {/* Header para mobile */}
      <div className="pc-header-mobile">
        <h2 className="pc-title">{title}</h2>
        {subtitle && <p className="pc-subtitle">{subtitle}</p>}
      </div>

      <div className="pc-track-wrapper">
        {/* Flecha izquierda */}
        <button
          className={`pc-arrow pc-arrow-left ${isHovering && canScrollLeft ? 'visible' : ''}`}
          onClick={() => scroll('prev')}
          aria-label="Anterior"
        >
          <svg width="16" height="16" viewBox="0 0 50 50">
            <polygon points="25,31.3 4.2,10.5 0.1,14.6 25,39.5 50,14.6 45.9,10.5" transform="rotate(90 25 25)" />
          </svg>
        </button>

        <div className="pc-track" ref={carouselRef}>
          {/* Intro - solo visible en desktop */}
          <div className="pc-item pc-item-intro">
            <div className="pc-intro-content">
              <h2 className="pc-title">{title}</h2>
              {subtitle && <p className="pc-subtitle">{subtitle}</p>}
            </div>
          </div>

          {/* Productos · usan el mismo ProductCard que el grid de curated-selection */}
          {products.map((product, index) => (
            <div
              key={product.id || index}
              className="pc-item pc-item-card"
            >
              <ProductCard product={product} minimal={minimal} />
            </div>
          ))}
        </div>

        {/* Flecha derecha */}
        <button
          className={`pc-arrow pc-arrow-right ${isHovering && canScrollRight ? 'visible' : ''}`}
          onClick={() => scroll('next')}
          aria-label="Siguiente"
        >
          <svg width="16" height="16" viewBox="0 0 50 50">
            <polygon points="25,31.3 4.2,10.5 0.1,14.6 25,39.5 50,14.6 45.9,10.5" transform="rotate(-90 25 25)" />
          </svg>
        </button>
      </div>

      {/* Barra de progreso */}
      <div className="pc-progress-container">
        <div className="pc-progress-track">
          <div
            className="pc-progress-bar"
            style={{ left: `${scrollProgress * 0.7}%` }}
          />
        </div>
      </div>
    </div>
  );
}
